import React, { useEffect, useState } from "react";
import umsIcon from "../../../../assets/images/icons/umsIcon.svg";
import useConfig from "../../../../hooks/useConfig";
import crmIcon from "../../../../assets/images/icons/crmIcon.svg";
import edmsIcon from "../../../../assets/images/icons/edmsIcon.svg";
import subscriptionIcon from "../../../../assets/images/icons/subscriptionIcon.svg";
import workflowIcon from "../../../../assets/images/icons/workflowIcon.svg";
import requestIcon from "../../../../assets/images/icons/requestIcon.svg";
import serviceIcon from "../../../../assets/images/icons/serviceIcon.svg";
import { toast } from "react-toastify";
import MainServices from "../../../../services/MainServices";
import PageLoader from "../../../../assets/loaders/PageLoading";
import PermissionContextFunctions from "../../../../assets/functions/PermissionContextFunctions";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import * as Storage from "../../../../store/LocalStorage";

const ActionItemsModal = ({ isOpen, onClose }) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const { getUmsAuthProvider } = MainServices();
  const config = useConfig();
  const [modules, setModules] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { grantModulePermissions } = PermissionContextFunctions();
  const [canAccessEmployee, setCanAcceessEmployee] = useState(false);

  const iconMap = {
    UMS: umsIcon,
    CRM: crmIcon,
    EDMS: edmsIcon,
    WORKFLOW: workflowIcon,
    SUBSCRIPTION: subscriptionIcon,
    CLIENT: crmIcon,
    CUSTOMER: crmIcon,
    REQUEST: requestIcon,
    SERVICE: serviceIcon,
  };

  const getIconForModule = (name) => iconMap[name] || umsIcon;

  useEffect(() => {
    getAuthProviders();
  }, []);

  useEffect(() => {
    const isAccess = Storage.get(config?.canAccessEmployeeKey);
    setCanAcceessEmployee(isAccess);
  }, [Storage.get(config?.canAccessEmployeeKey)]);

  const getAuthProviders = async () => {
    setLoading(true);
    const details = {
      filters: [{ key: "status", eq: "active" }],
      isAllowed: true,
    };
    try {
      let res = await getUmsAuthProvider(details);

      if (res.data.success) {
        const authProviders = res?.data?.data?.doc || [];
        const modulesFormatted = authProviders.map((authProvider) => ({
          id: authProvider?.publicId,
          label: authProvider?.labelName,
          name: authProvider?.providerName,
          redirect: authProvider?.redirectUri || "",
          iconSrc: getIconForModule(authProvider?.providerName),
          iconAlt: `${authProvider?.providerName} Icon`,
        }));
        setError("");
        setModules(modulesFormatted);
      }
    } catch (error) {
      setError(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleModuleClick = (redirectUrl) => {
    if (!redirectUrl) {
      toast.warn("Redirection URL is missing");
      return;
    }

    const currentPath = window.location.pathname;
    const redirectPath = new URL(redirectUrl).pathname;

    // Check if current path starts with the module path
    // (e.g., "/app/dashboard" starts with "/app")
    if (currentPath.startsWith(redirectPath)) {
      toast.info("You're already in this module");
    } else {
      window.open(redirectUrl, "_self");
    }
  };

  const checkModuleAccess = (moduleName) => {
    // Check if user has any module permissions (excluding UMS)
    const hasAnyModulePermission = modules.some(
      (m) => m.name !== "UMS" && grantModulePermissions(m.name)
    );

    if (moduleName === "UMS") {
      if (hasAnyModulePermission) {
        // Has other permissions - UMS must be explicitly granted
        return grantModulePermissions("UMS");
      } else {
        // No other permissions - check canAccessEmployee
        return canAccessEmployee === "true";
      }
    }

    return grantModulePermissions(moduleName);
  };

  return (
    <div
      className="fixed inset-0 z-[1002] flex items-center justify-center"
      style={{ backgroundColor: "rgba(4, 8, 11, 0.35)" }}
      onClick={onClose}
    >
      <div
        className="w-[90%] max-w-6xl h-[90%] bg-white rounded-xl shadow-xl overflow-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={onClose}
            className="p-2 flex justify-center align-middle bg-[#f2f4f7] rounded-lg w-[40px] hover:bg-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-black"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900 m-0">
            {t("Action Items")}
          </h2>
        </div>

        {/* Grid */}
        {/* Loader */}
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <PageLoader />
          </div>
        ) : error ? (
          // Error display
          <div className="flex justify-center items-center h-full">
            <p className="text-red-500 font-medium text-center">{error}</p>
          </div>
        ) : (
          // Main content grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((module, index) => {
              const hasAccess = checkModuleAccess(module.name);

              if (!hasAccess) return;

              return (
                <div
                  key={index}
                  onClick={() =>
                    hasAccess && handleModuleClick(module.redirect)
                  }
                  className={`relative group flex items-center gap-3 px-4 h-[84px] bg-[#F9FAFB] border border-[#F2F4F7] rounded-xl transition-all duration-200 ${
                    hasAccess
                      ? "cursor-pointer hover:shadow-md"
                      : "cursor-not-allowed"
                  }`}
                >
                  {/* Soft blur layer */}
                  {!hasAccess && (
                    <div className="absolute inset-0 z-10 bg-white/50 backdrop-blur-[1px] rounded-xl pointer-events-none" />
                  )}

                  {/* No Access Badge */}
                  {!hasAccess && (
                    <div className="absolute top-2 right-2 z-20">
                      <span className="text-[10px] font-medium text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] px-2 py-0.5 rounded-full shadow-sm">
                        {t("No Access")}
                      </span>
                    </div>
                  )}

                  {/* Icon and Module Name */}
                  <div className="w-[40px] h-[40px] bg-[#f2f4f7] rounded-[8px] flex justify-center items-center z-0">
                    <img
                      src={module.iconSrc}
                      alt={module.iconAlt}
                      className="w-[24px]"
                    />
                  </div>
                  <span
                    className={`text-sm font-medium z-0 ${
                      hasAccess ? "text-[#04080B]" : "text-gray-500"
                    }`}
                  >
                    {t(module.label)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionItemsModal;
